/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-30
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import {
  useCreateSetupIntentMutation,
  useAddPaymentMethodMutation,
} from '../../../../features/payment/paymentApi';
import InternationalAddressForm from '../../../common/InternationalAddressForm';
import type { InternationalAddressData } from '../../../common/InternationalAddressForm';
import { SecurePaymentProvider } from '../../../payment/providers/SecurePaymentProvider';

interface AddPaymentMethodModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingPaymentMethod?: any; // Payment method to update (pre-fills billing info)
}

/**
 * Inner component that uses Stripe hooks - must be wrapped by SecurePaymentProvider
 */
const PaymentMethodForm: React.FC<
  AddPaymentMethodModalProps & { isUpdatingExisting: boolean }
> = ({ onClose, onSuccess, existingPaymentMethod, isUpdatingExisting }) => {
  // Stripe hooks - these require SecurePaymentProvider
  const stripe = useStripe();
  const elements = useElements();

  // Form state
  const [cardholderName, setCardholderName] = useState('');
  const [billingAddress, setBillingAddress] =
    useState<InternationalAddressData>({
      line1: '',
      line2: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'United States',
    });

  // API mutations
  const [createSetupIntent, { isLoading: isCreatingSetup }] =
    useCreateSetupIntentMutation();
  const [addPaymentMethod, { isLoading: isAddingMethod }] =
    useAddPaymentMethodMutation();

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'form' | 'processing' | 'success'>('form');
  const [cardError, setCardError] = useState<string | null>(null);

  const isLoading = isCreatingSetup || isAddingMethod;

  // Pre-fill form when modal opens with existing payment method
  useEffect(() => {
    if (existingPaymentMethod) {
      // Pre-fill cardholder name if available
      if (existingPaymentMethod.billing_address?.name) {
        setCardholderName(existingPaymentMethod.billing_address.name);
      }

      // Pre-fill billing address from existing payment method
      if (existingPaymentMethod.billing_address) {
        const addr = existingPaymentMethod.billing_address;
        setBillingAddress({
          line1: addr.line1 || '',
          line2: addr.line2 || '',
          city: addr.city || '',
          state: addr.state || '',
          postalCode: addr.postal_code || '',
          country:
            addr.country === 'US'
              ? 'United States'
              : addr.country || 'United States',
        });
      }
    }
  }, [existingPaymentMethod]);

  // Reset form state
  const handleReset = () => {
    setCardholderName('');
    setBillingAddress({
      line1: '',
      line2: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'United States',
    });
    setError(null);
    setCardError(null);
    setStep('form');
  };

  // Standard Stripe form submission handler following Stripe's recommended pattern
  const handleSubmit = async () => {
    if (!stripe || !elements) {
      setError('Stripe has not loaded properly. Please refresh and try again.');
      return;
    }

    setError(null);
    setCardError(null);
    // Keep form visible during processing - don't change step yet

    try {
      // Create a setup intent first to get a client secret
      const setupIntentResult = await createSetupIntent({}).unwrap();

      if (!setupIntentResult.client_secret) {
        throw new Error('Failed to create setup intent');
      }

      // Get CardElement using standard Stripe pattern while it's still mounted
      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Use Stripe to confirm the setup intent and create a payment method
      const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(
        setupIntentResult.client_secret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: cardholderName,
              address: {
                line1: billingAddress.line1,
                line2: billingAddress.line2 || undefined,
                city: billingAddress.city,
                state: billingAddress.state,
                postal_code: billingAddress.postalCode,
                country:
                  billingAddress.country === 'United States'
                    ? 'US'
                    : billingAddress.country,
              },
            },
          },
        }
      );

      if (stripeError) {
        throw new Error(
          stripeError.message || 'Failed to create payment method'
        );
      }

      if (!setupIntent?.payment_method) {
        throw new Error('No payment method was created');
      }

      // Now use the real payment method ID from Stripe (simplified single payment method approach)
      await addPaymentMethod({
        stripe_payment_method_id: setupIntent.payment_method as string,
        set_as_default: true, // Always set as default since we only support one payment method
      }).unwrap();

      // Only change step after all API calls are complete
      setStep('success');

      // Close modal and refresh data after brief success display
      setTimeout(() => {
        onSuccess();
        onClose();
        handleReset();
      }, 1500);
    } catch (err: any) {
      setError(
        err?.message ||
          err?.data?.detail ||
          'Failed to add payment method. Please try again.'
      );
      // Stay on form step to show error
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!isLoading) {
      onClose();
      handleReset();
    }
  };

  // Helper function for button text
  const getButtonText = () => {
    if (isLoading) {
      return isUpdatingExisting ? 'Updating...' : 'Adding...';
    }
    return isUpdatingExisting ? 'Update Payment Method' : 'Add Payment Method';
  };

  // Validation
  const isFormValid =
    cardholderName.trim().length > 0 &&
    billingAddress.line1.trim().length > 0 &&
    billingAddress.city.trim().length > 0 &&
    billingAddress.state.trim().length > 0 &&
    billingAddress.postalCode.trim().length > 0 &&
    stripe &&
    elements;

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {step === 'success' && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="success.main" gutterBottom>
            {isUpdatingExisting
              ? 'Payment Method Updated!'
              : 'Payment Method Added!'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isUpdatingExisting
              ? 'Your payment method has been updated successfully.'
              : 'Your new payment method has been added successfully.'}
          </Typography>
        </Box>
      )}

      {step === 'form' && (
        <>
          <Box
            component="form"
            sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
          >
            {/* Card Information */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Card Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Standard Stripe CardElement */}
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    '& .StripeElement': {
                      minHeight: '40px',
                      padding: '10px 12px',
                      fontSize: '16px',
                    },
                  }}
                >
                  <CardElement
                    options={{
                      hidePostalCode: true,
                      style: {
                        base: {
                          fontSize: '16px',
                          color: '#424770',
                          '::placeholder': {
                            color: '#aab7c4',
                          },
                        },
                        invalid: {
                          color: '#9e2146',
                        },
                      },
                    }}
                    onReady={() => {
                      console.log('✅ CardElement mounted and ready');
                    }}
                    onChange={(event: any) => {
                      setCardError(event.error ? event.error.message : null);
                    }}
                  />
                </Box>
                {cardError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {cardError}
                  </Alert>
                )}
                <TextField
                  fullWidth
                  label="Cardholder Name"
                  value={cardholderName}
                  onChange={e => setCardholderName(e.target.value)}
                  placeholder="John Doe"
                  required
                />
              </Box>
            </Box>

            {/* Billing Address - International Support */}
            <InternationalAddressForm
              value={billingAddress}
              onChange={setBillingAddress}
              sectionTitle="Billing Address"
              required
              requirePostalCode
              showGuidance={false}
              fieldNames={{
                line1: 'Street Address',
                postalCode: 'ZIP/Postal Code',
              }}
              disabled={isLoading}
            />

            {/* Beta Mode Notice */}
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Beta Mode:</strong> We collect your card information for
                billing setup, but your card will not be charged while the site
                is in Beta.
              </Typography>
            </Alert>
          </Box>

          <Box
            sx={{
              mt: 3,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <Button onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!isFormValid || isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
            >
              {getButtonText()}
            </Button>
          </Box>
        </>
      )}
    </>
  );
};

/**
 * Modal for adding new payment methods with Stripe integration
 * Handles card input, billing address, and payment method verification
 */
const AddPaymentMethodModal: React.FC<AddPaymentMethodModalProps> = ({
  open,
  onClose,
  onSuccess,
  existingPaymentMethod,
}) => {
  // Determine if we're updating or adding
  const isUpdatingExisting = !!existingPaymentMethod;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: '90vh' },
      }}
    >
      <DialogTitle>
        {isUpdatingExisting ? 'Update Payment Method' : 'Add Payment Method'}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {isUpdatingExisting
            ? 'Enter your new card details. Your billing address is pre-filled but you can update it if needed.'
            : 'Add a new card or bank account for payments'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, overflow: 'auto' }}>
        <SecurePaymentProvider>
          <PaymentMethodForm
            open={open}
            onClose={onClose}
            onSuccess={onSuccess}
            existingPaymentMethod={existingPaymentMethod}
            isUpdatingExisting={isUpdatingExisting}
          />
        </SecurePaymentProvider>
      </DialogContent>
    </Dialog>
  );
};

export default AddPaymentMethodModal;
